'use client';

import { useState, useEffect, useRef } from 'react';
import { fetchCalendarData } from '@/services/api';
import React from 'react';
import { useLanguage } from '@/contexts/LanguageContext';

const CustomCalendar = ({ 
  onDateSelect, 
  disabledDates = [],
  guests = 2,
  children = 0,
  roomCount = 1,
  onClose,
  closeNavbar,
  propertyCode
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [calendar1, setCalendar1] = useState([]);
  const [calendar2, setCalendar2] = useState([]);
  const [prices, setPrices] = useState({});
  const [discountMap, setDiscountMap] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [apiData, setApiData] = useState(null);
  const [weeklyAverages, setWeeklyAverages] = useState({});
  const [isMobile, setIsMobile] = useState(false);
  const [showTwoMonths, setShowTwoMonths] = useState(true);
  const [fetchedMonths, setFetchedMonths] = useState([]);
  const [error, setError] = useState(null);
  const [colorMap, setColorMap] = useState({});
  const { t, language } = useLanguage();
  
  // Derived values for booking summary
  const checkIn = selectedStartDate;
  const checkOut = selectedEndDate;
  
  // Calculate if current month is the current real-world month
  const today = new Date();
  const isCurrentMonth = currentDate.getMonth() === today.getMonth() && currentDate.getFullYear() === today.getFullYear();
  
  // Functions to navigate between months
  const goToPreviousMonth = () => {
    if (isCurrentMonth) return; // Don't go before current month
    
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() - 1);
    handleMonthChange(newDate);
  };
  
  const goToNextMonth = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + 1);
    handleMonthChange(newDate);
  };
  
  // Tutup navbar saat komponen dimount
  useEffect(() => {
    // Jika ada fungsi closeNavbar, panggil untuk menutup navbar
    if (typeof closeNavbar === 'function') {
      closeNavbar();
    }
  }, [closeNavbar]);
  
  // DAYS untuk desktop dan mobile - both start with Sunday to match calendar grid
  const DAYS = isMobile 
    ? ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] 
    : ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
  const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const months = MONTHS; // Add alias for compatibility with the rendering code

  // Deteksi perangkat mobile
  useEffect(() => {
    const checkIsMobile = () => {
      const mobileView = window.innerWidth < 768;
      setIsMobile(mobileView);
      setShowTwoMonths(!mobileView); // Update showTwoMonths based on screen size
    };
    
    // Cek saat komponen mount
    checkIsMobile();
    
    // Cek ketika window resize
    window.addEventListener('resize', checkIsMobile);
    
    // Cleanup
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // Format tanggal untuk API
  const formatDate = (date) => {
    if (!date) return '';
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  // Format tanggal untuk tampilan user (31 March, 2025)
  const formatDateForDisplay = (date) => {
    if (!date) return '';
    return `${date.getDate()} ${MONTHS[date.getMonth()]}, ${date.getFullYear()}`;
  };

  // Calculate the number of nights between two dates
  const calculateNights = () => {
    if (!selectedStartDate || !selectedEndDate) return 0;
    const diffTime = Math.abs(selectedEndDate - selectedStartDate);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  // Fungsi untuk mengecek apakah tanggal adalah weekend (Sabtu atau Minggu)
  const isWeekend = (date) => {
    const day = date.getDay();
    return day === 0 || day === 6; // 0 = Minggu, 6 = Sabtu
  };

  // Fungsi untuk mendapatkan kunci minggu (tanggal Senin dari minggu yang sama)
  const getWeekKey = (date) => {
    const dayOfWeek = date.getDay() || 7; // Convert Sunday (0) to 7
    const mondayDate = new Date(date);
    mondayDate.setDate(date.getDate() - dayOfWeek + 1);
    return formatDate(mondayDate);
  };

  // Fungsi untuk menghitung rata-rata harga mingguan
  const calculateWeeklyAverages = (priceMap) => {
    const weeklyPrices = {};
    const weeklyAvgs = {};
    
    // Kelompokkan tanggal berdasarkan minggu
    Object.entries(priceMap).forEach(([dateStr, price]) => {
      if (!dateStr || !price) return;
      
      const date = new Date(dateStr);
      const weekKey = getWeekKey(date);
      
      if (!weeklyPrices[weekKey]) {
        weeklyPrices[weekKey] = [];
      }
      
      weeklyPrices[weekKey].push(price);
    });
    
    // Hitung rata-rata untuk setiap minggu
    Object.entries(weeklyPrices).forEach(([weekKey, prices]) => {
      if (prices.length === 0) return;
      const sum = prices.reduce((acc, price) => acc + price, 0);
      weeklyAvgs[weekKey] = Math.round(sum / prices.length);
    });
    
    return weeklyAvgs;
  };

  // Tambahkan fungsi generateCalendar yang hilang
  const generateCalendar = (year, month) => {
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    // Buat array kosong untuk kalender
    const calendar = [];
    let week = [];
    
    // Isi dengan null untuk hari-hari sebelum hari pertama bulan
    for (let i = 0; i < firstDay; i++) {
      week.push(null);
    }
    
    // Isi dengan tanggal-tanggal dalam bulan
    for (let day = 1; day <= daysInMonth; day++) {
      week.push(day);
      
      // Jika sudah 7 hari atau hari terakhir bulan, tambahkan ke kalender
      if (week.length === 7 || day === daysInMonth) {
        // Jika belum 7 hari tapi sudah hari terakhir, tambahkan null
        while (week.length < 7) {
          week.push(null);
        }
        
        calendar.push(week);
        week = [];
      }
    }
    
    return calendar;
  };

  // Fungsi untuk memperbarui kalender
  const updateCalendars = () => {
    console.log(`Updating calendars for ${MONTHS[currentDate.getMonth()]} ${currentDate.getFullYear()} and next month`);
    
    // Generate calendar for current month
    const cal1 = generateCalendar(currentDate.getFullYear(), currentDate.getMonth());
    
    // Generate calendar for next month
    const nextMonth = new Date(currentDate);
    nextMonth.setMonth(currentDate.getMonth() + 1);
    const cal2 = generateCalendar(nextMonth.getFullYear(), nextMonth.getMonth());
    
    setCalendar1(cal1);
    setCalendar2(cal2);
    
    console.log('Calendars updated');
  };

  // Tambahkan useEffect untuk memanggil fetchRoomData saat komponen dimount
  useEffect(() => {
    // Set default date to today
    const today = new Date();
    setCurrentDate(today);
    
    fetchRoomData();
  }, []);

  // Perbaiki fungsi fetchRoomData untuk menghitung harga termurah dari semua kamar
  const fetchRoomData = async (startDate, endDate) => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Jika tidak ada tanggal yang diberikan, gunakan tanggal hari ini dan 2 bulan ke depan
      if (!startDate) {
        startDate = new Date();
      }
      
      if (!endDate) {
        endDate = new Date(startDate);
        // Add 2 months to the end date
        endDate.setMonth(endDate.getMonth() + 2);
        endDate.setDate(0); // Last day of the month
      }
      
      const checkInDate = formatDate(startDate);
      const checkOutDate = formatDate(endDate);
      
      console.log(`Fetching calendar data for ${checkInDate} to ${checkOutDate}`);
      
      try {
        // Gunakan fetchCalendarData yang baru
        const response = await fetchCalendarData(
          checkInDate,
          checkOutDate,
          guests,
          children,
          roomCount,
          propertyCode
        );
        
        if (response && response.status === 'success' && Array.isArray(response.data)) {
          // Store the full response data for reference
          setApiData(response.data);
          
          // Debug: Log the entire response structure
          console.log('API Response Data Structure:');
          console.log('Number of rooms:', response.data.length);
          
          // Log the first room's structure in detail
          if (response.data.length > 0) {
            const firstRoom = response.data[0];
            console.log('First room structure:', JSON.stringify(firstRoom, null, 2));
            
            // Specifically log the dates and rates structures
            if (firstRoom.availability && firstRoom.availability.dates) {
              console.log('First room availability dates structure:');
              console.log('Number of dates:', firstRoom.availability.dates.length);
              if (firstRoom.availability.dates.length > 0) {
                console.log('First date structure:', JSON.stringify(firstRoom.availability.dates[0], null, 2));
                
                // Check if dates have a rates array
                if (firstRoom.availability.dates[0].rates) {
                  console.log('First date rates structure:', JSON.stringify(firstRoom.availability.dates[0].rates, null, 2));
                } else {
                  console.log('Note: Dates do not have a rates array');
                }
              }
            }
            
            // Log the rate_details structure
            if (firstRoom.rate_details) {
              console.log('Rate details structure:', JSON.stringify(firstRoom.rate_details, null, 2));
            }
            
            // Log the available_rates structure
            if (firstRoom.available_rates) {
              console.log('Available rates structure:');
              console.log('Number of available rates:', firstRoom.available_rates.length);
              if (firstRoom.available_rates.length > 0) {
                console.log('First available rate:', JSON.stringify(firstRoom.available_rates[0], null, 2));
              }
            }
          }
          
          // Buat objek untuk menyimpan semua harga per tanggal
          const allPricesPerDate = {};
          
          // Process all dates for all rooms
          response.data.forEach(room => {
            // First log this room's rate codes for debugging
            console.log(`Processing room ${room.room_info?.room_id} with rate codes:`, 
              room.available_rates?.map(r => r.ratecode) || 'No available_rates',
              'Rate details:', room.rate_details?.ratecode || 'No rate_details'
            );

            // Process dates from availability.dates
            if (room && room.availability && Array.isArray(room.availability.dates)) {
              const inventory = room.availability.dates;
              
              inventory.forEach(item => {
                if (item.date) {
                  const datePart = item.date.split('T')[0];
                  
                  // Initialize array for this date if not exists
                  if (!allPricesPerDate[datePart]) {
                    allPricesPerDate[datePart] = [];
                  }
                  
                  // Log what we found for debugging
                  console.log(`Date ${datePart} from room ${room.room_info?.room_id}:`, 
                    'final_rate:', item.final_rate,
                    'rooms_to_sell:', item.rooms_to_sell,
                    'available_rates:', item.available_rates
                  );

                  // PRIORITY: Check available_rates array within this date (THIS IS THE KEY FIX)
                  if (item.available_rates && Array.isArray(item.available_rates)) {
                    item.available_rates.forEach(rate => {
                      // Filter out inactive rates (is_active: 0 = inactive, 1 = active)
                      if (rate && rate.final_rate > 0 && rate.is_active !== 0) {
                        const price = parseFloat(rate.final_rate);
                        if (!isNaN(price)) {
                          console.log(`Found price ${price} for date ${datePart} from available_rates, ratecode: ${rate.ratecode}`);
                          
                          // Add this price to the array
                          allPricesPerDate[datePart].push({
                            price: price,
                            color: rate.color || '#363738',
                            ratecode: rate.ratecode || 'Unknown',
                            discountInfo: rate.discount,
                            isPriority: rate.ratecode === 'TIKET_V2',
                            includeBreakfast: rate.include_breakfast === 1
                          });
                        }
                      }
                    });
                  }
                  
                  // Also check if there's a direct final_rate that's positive (fallback)
                  if (item.final_rate > 0 && item.rooms_to_sell > 0) {
                    const price = parseFloat(item.final_rate);
                    if (!isNaN(price)) {
                      console.log(`Found direct price ${price} for date ${datePart}`);
                      
                      // Add this price to the array
                      allPricesPerDate[datePart].push({
                        price: price,
                        color: item.color,
                        ratecode: item.ratecode || 'Default',
                        discountInfo: item.discount,
                        isPriority: false
                      });
                    }
                  }
                  
                  // Check if there are rates inside the date item (another fallback)
                  if (item.rates && Array.isArray(item.rates)) {
                    item.rates.forEach(rate => {
                      if (rate && rate.final_rate > 0 && rate.rooms_to_sell > 0) {
                        const price = parseFloat(rate.final_rate);
                        if (!isNaN(price)) {
                          console.log(`Found rate price ${price} for date ${datePart} from rate ${rate.ratecode}`);
                          
                          // Add this price to the array
                          allPricesPerDate[datePart].push({
                            price: price,
                            color: rate.color,
                            ratecode: rate.ratecode || 'Unknown',
                            discountInfo: rate.discount,
                            isPriority: rate.ratecode === 'TIKET_V2'
                          });
                        }
                      }
                    });
                  }
                }
              });
            }
            
            // Also check available_rates
            if (room.available_rates && Array.isArray(room.available_rates)) {
              // First check if there's a TIKET_V2 rate that is active
              const tiketV2Rate = room.available_rates.find(r => r.ratecode === 'TIKET_V2' && r.final_rate > 0 && r.is_active !== 0);
              
              if (tiketV2Rate) {
                console.log(`Found TIKET_V2 rate in room's available_rates:`, tiketV2Rate);
                
                // Find all dates for this room
                if (room.availability && Array.isArray(room.availability.dates)) {
                  room.availability.dates.forEach(dateItem => {
                    if (dateItem.date) {
                      const datePart = dateItem.date.split('T')[0];
                      
                      // Initialize array for this date if not exists
                      if (!allPricesPerDate[datePart]) {
                        allPricesPerDate[datePart] = [];
                      }
                      
                      // Add the TIKET_V2 price for this date with high priority
                      const price = parseFloat(tiketV2Rate.final_rate);
                      if (!isNaN(price)) {
                        console.log(`Adding TIKET_V2 price ${price} to date ${datePart} from room's available_rates`);
                        
                        allPricesPerDate[datePart].push({
                          price: price,
                          color: tiketV2Rate.color || '#059669',
                          ratecode: 'TIKET_V2',
                          discountInfo: tiketV2Rate.discount,
                          isPriority: true  // Mark as priority rate
                        });
                      }
                    }
                  });
                }
              }
              
              // Process other rates if needed
              room.available_rates.forEach(rate => {
                // Skip TIKET_V2 as we already processed it, and skip inactive rates
                if (rate && rate.ratecode !== 'TIKET_V2' && rate.final_rate > 0 && rate.is_active !== 0) {
                  console.log(`Found rate with price ${rate.final_rate}, code: ${rate.ratecode || 'Unknown'}`);
                  
                  // Determine what date this rate applies to
                  let datePart = null;
                  if (rate.date) {
                    datePart = rate.date.split('T')[0];
                  } else if (rate.check_in) {
                    datePart = rate.check_in.split('T')[0];
                  } else if (room.availability && room.availability.check_in) {
                    datePart = room.availability.check_in.split('T')[0];
                  }
                  
                  if (datePart) {
                    // Initialize array for this date if not exists
                    if (!allPricesPerDate[datePart]) {
                      allPricesPerDate[datePart] = [];
                    }
                    
                    const price = parseFloat(rate.final_rate);
                    if (!isNaN(price)) {
                      console.log(`Storing rate price ${price} for date ${datePart}`);
                      
                      // Add this price to the array
                      allPricesPerDate[datePart].push({
                        price: price,
                        color: rate.color,
                        ratecode: rate.ratecode || 'Unknown',
                        discountInfo: rate.discount,
                        isPriority: false
                      });
                    }
                  }
                }
              });
            }
            
            // Also check rate_details
            if (room.rate_details && room.rate_details.final_rate > 0) {
              // Determine what date this rate applies to
              let datePart = null;
              if (room.rate_details.date) {
                datePart = room.rate_details.date.split('T')[0];
              } else if (room.rate_details.check_in) {
                datePart = room.rate_details.check_in.split('T')[0];
              } else if (room.availability && room.availability.check_in) {
                datePart = room.availability.check_in.split('T')[0];
              }
              
              if (datePart) {
                // Initialize array for this date if not exists
                if (!allPricesPerDate[datePart]) {
                  allPricesPerDate[datePart] = [];
                }
                
                const price = parseFloat(room.rate_details.final_rate);
                if (!isNaN(price)) {
                  console.log(`Storing rate_details price ${price} for date ${datePart} from rate ${room.rate_details.ratecode}`);
                  
                  // Add this price to the array
                  allPricesPerDate[datePart].push({
                    price: price,
                    color: room.rate_details.color,
                    ratecode: room.rate_details.ratecode || 'Unknown',
                    discountInfo: room.rate_details.discount,
                    isPriority: room.rate_details.ratecode === 'TIKET_V2'
                  });
                }
              }
            }
          });
          
          // Now process all collected prices, prioritizing TIKET_V2 rates
          const newPriceMap = {};
          const newColorMap = {};
          const newDiscountMap = {};
          
          console.log("All collected prices per date:", allPricesPerDate);
          
          // Process all prices to find the best price for each date (prioritizing TIKET_V2)
          Object.keys(allPricesPerDate).forEach(datePart => {
            const pricesForDate = allPricesPerDate[datePart];
            
            if (pricesForDate.length > 0) {
              // First check if there's any TIKET_V2 rate for this date
              const tiketV2Rates = pricesForDate.filter(p => p.ratecode === 'TIKET_V2' && p.price > 0);
              
              if (tiketV2Rates.length > 0) {
                // If we have TIKET_V2 rates, use the lowest one
                tiketV2Rates.sort((a, b) => a.price - b.price);
                const lowestTiketV2 = tiketV2Rates[0];
                
                console.log(`For date ${datePart}, found ${tiketV2Rates.length} TIKET_V2 prices, lowest: ${lowestTiketV2.price}`);
                
                newPriceMap[datePart] = lowestTiketV2.price;
                if (lowestTiketV2.color) newColorMap[datePart] = lowestTiketV2.color;
                if (lowestTiketV2.discountInfo) {
                  newDiscountMap[datePart] = {
                    value: lowestTiketV2.discountInfo.percent,
                    rateAfterDiscount: lowestTiketV2.price
                  };
                }
              } else {
                // If no TIKET_V2 rates, use the lowest non-zero price
                const nonZeroPrices = pricesForDate.filter(p => p.price > 0);
                if (nonZeroPrices.length > 0) {
                  nonZeroPrices.sort((a, b) => a.price - b.price);
                  const lowestPrice = nonZeroPrices[0];
                  
                  console.log(`For date ${datePart}, no TIKET_V2 rates, using lowest price: ${lowestPrice.price}`);
                  
                  newPriceMap[datePart] = lowestPrice.price;
                  if (lowestPrice.color) newColorMap[datePart] = lowestPrice.color;
                  if (lowestPrice.discountInfo) {
                    newDiscountMap[datePart] = {
                      value: lowestPrice.discountInfo.percent,
                      rateAfterDiscount: lowestPrice.price
                    };
                  }
                }
              }
            }
          });
          
          // Update state with the new data
          setPrices(newPriceMap);
          setDiscountMap(newDiscountMap);
          setColorMap(newColorMap);
          
          // Calculate and set weekly averages
          const weeklyAvgs = calculateWeeklyAverages(newPriceMap);
          setWeeklyAverages(weeklyAvgs);
          
          // Log the processed data
          console.log('Processed price map:', newPriceMap);
          console.log('Weekly averages:', weeklyAvgs);
          
          // Generate calendar based on the new data
          generateCalendars();
        } else {
          console.error('Invalid response format from API:', response);
          setError('Terjadi kesalahan saat mengambil data. Silakan coba lagi.');
        }
      } catch (error) {
        console.error('Error fetching calendar data:', error);
        setError('Terjadi kesalahan saat mengambil data. Silakan coba lagi.');
      }
    } catch (error) {
      console.error('Error in fetchRoomData:', error);
      setError('Terjadi kesalahan saat mengambil data. Silakan coba lagi.');
    } finally {
      setIsLoading(false);
    }
  };

  // Fungsi untuk mengambil data tambahan jika diperlukan
  const fetchAdditionalMonths = async (startMonth, endMonth) => {
    try {
      setIsLoading(true);
      
      // Ambil data untuk bulan yang diminta
      const startDate = new Date(currentDate.getFullYear(), startMonth, 1);
      const endDate = new Date(currentDate.getFullYear(), endMonth + 1, 0);
      
      // Format tanggal untuk API
      const formattedStartDate = formatDate(startDate);
      const formattedEndDate = formatDate(endDate);
      
      console.log(`Fetching additional months: ${formattedStartDate} to ${formattedEndDate}`);
      
      // Gunakan fetchCalendarData yang baru
      const response = await fetchCalendarData(
        formattedStartDate,
        formattedEndDate,
        guests,
        children,
        roomCount
      );
      
      if (response && response.status === 'success' && Array.isArray(response.data)) {
        // Buat objek untuk menyimpan harga termurah per tanggal
        const newPriceMap = { ...prices };
        const newDiscountMap = { ...discountMap };
        const newColorMap = { ...colorMap };
        
        // Iterasi melalui semua kamar untuk menemukan harga termurah per tanggal
        response.data.forEach(room => {
          if (room && room.availability && Array.isArray(room.availability.dates)) {
            const inventory = room.availability.dates;
            
            // Isi priceMap, discountMap, dan colorMap dari inventory
            inventory.forEach(item => {
              if (item.date) {
                // Log the raw item to see its structure
                console.log(`Processing date item: ${JSON.stringify(item)}`);
                
                // Extract date part only (YYYY-MM-DD) from the full ISO date
                const datePart = item.date.split('T')[0];
                console.log(`Extracted date part: ${datePart}`);
                
                // Log the entire item for debugging
                console.log(`Full item data: ${JSON.stringify(item)}`);
                
                // Check if final_rate exists and is not null/undefined
                if (item.final_rate !== undefined && item.final_rate !== null) {
                  // Pastikan final_rate adalah angka
                  let price = item.final_rate;
                  
                  // Convert to number if it's a string
                  if (typeof price === 'string') {
                    price = parseFloat(price);
                    if (isNaN(price)) {
                      console.error(`Invalid price value: ${item.final_rate}`);
                      return; // Skip this item if price is not a valid number
                    }
                  }
                  
                  console.log(`Date: ${datePart}, final_rate: ${item.final_rate}, parsed price: ${price}`);
                  
                  // Jika tanggal belum ada di priceMap atau harga ini lebih murah, update
                  if (!newPriceMap[datePart] || price < newPriceMap[datePart]) {
                    newPriceMap[datePart] = price;
                    
                    // Simpan warna dari API untuk harga termurah
                    if (item.color) {
                      console.log(`Storing color for ${datePart}: ${item.color}`);
                      newColorMap[datePart] = item.color;
                    }
                    
                    // Simpan informasi diskon jika ada
                    if (item.discount) {
                      newDiscountMap[datePart] = {
                        value: item.discount.percent,
                        rateAfterDiscount: price
                      };
                    }
                  }
                  
                  // Tambahkan ke fetchedMonths jika belum ada
                  const month = datePart.substring(0, 7); // Format: YYYY-MM
                  if (!fetchedMonths.includes(month)) {
                    setFetchedMonths(prev => [...prev, month]);
                  }
                }
              }
            });
          }
        });
        
        // Update state dengan data yang diperoleh
        setPrices(newPriceMap);
        setDiscountMap(newDiscountMap);
        setColorMap(newColorMap);
        
        // Hitung rata-rata harga mingguan
        const avgPrices = calculateWeeklyAverages(newPriceMap);
        setWeeklyAverages(avgPrices);
        
        // Update calendars after fetching data
        updateCalendars();
      }
      
      setIsLoading(false);
    } catch (err) {
      console.error('Error fetching additional months:', err);
      setIsLoading(false);
    }
  };

  // Perbaiki fungsi handleMonthChange untuk fetch data saat bulan berubah
  const handleMonthChange = (newDate) => {
    setCurrentDate(newDate);
    
    // Determine the start and end dates for the 2-month period
    const startMonth = newDate.getMonth();
    const startYear = newDate.getFullYear();
    
    // Create start date (1st of the month)
    const startDate = new Date(startYear, startMonth, 1);
    
    // Create end date (last day of the next month)
    const endDate = new Date(startYear, startMonth + 2, 0);
    
    console.log(`Fetching data for new period: ${formatDate(startDate)} to ${formatDate(endDate)}`);
    
    // Fetch data for the new 2-month period
    fetchRoomData(startDate, endDate);
  };

  // Tambahkan fungsi untuk memastikan kalender diperbarui setelah data harga diambil
  const generateCalendars = () => {
    // Generate calendar untuk bulan saat ini
    const cal1 = generateCalendar(currentDate.getFullYear(), currentDate.getMonth());
    setCalendar1(cal1);
    
    const nextMonth = new Date(currentDate);
    nextMonth.setMonth(currentDate.getMonth() + 1);
    const cal2 = generateCalendar(nextMonth.getFullYear(), nextMonth.getMonth());
    setCalendar2(cal2);
    
    console.log(`Generated calendars for ${MONTHS[currentDate.getMonth()]} and ${MONTHS[nextMonth.getMonth()]}`);
  };

  // Function to render a specific month's calendar
  const renderCalendar = (month, year) => {
    const calendarData = month === currentDate.getMonth() && year === currentDate.getFullYear() ? calendar1 : calendar2;
    return renderMonth(calendarData, new Date(year, month, 1));
  };

  // Perbaiki fungsi isDateDisabled untuk memeriksa ketersediaan kamar
  const isDateDisabled = (date) => {
    const dateString = formatDate(date);
    
    // Cek apakah tanggal ada dalam daftar tanggal yang dinonaktifkan
    if (disabledDates && disabledDates.some(disabledDate => 
      formatDate(new Date(disabledDate)) === dateString
    )) {
      return true;
    }
    
    // Cek apakah tanggal sudah lewat (sebelum hari ini)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date < today) {
      return true;
    }
    
    // Check if apiData contains rooms with availability for this date
    if (apiData && Array.isArray(apiData)) {
      for (const room of apiData) {
        // Check in availability.dates
        if (room && room.availability && Array.isArray(room.availability.dates)) {
          const dateEntry = room.availability.dates.find(day => {
            const dayDatePart = day.date?.split('T')[0];
            return dayDatePart === dateString;
          });
          
          // PRIORITY: Check available_rates array within this date (THIS IS THE KEY FIX)
          if (dateEntry && dateEntry.available_rates && Array.isArray(dateEntry.available_rates)) {
            for (const rate of dateEntry.available_rates) {
              if (rate && rate.final_rate > 0 && rate.is_active !== 0) {
                return false; // Date is not disabled
              }
            }
          }
          
          // Check direct final_rate (fallback)
          if (dateEntry && dateEntry.final_rate > 0 && dateEntry.rooms_to_sell > 0) {
            return false; // Date is not disabled
          }
          
          // Check rates array (another fallback)
          if (dateEntry && dateEntry.rates && Array.isArray(dateEntry.rates)) {
            for (const rate of dateEntry.rates) {
              if (rate && rate.final_rate > 0 && rate.rooms_to_sell > 0) {
                return false; // Date is not disabled
              }
            }
          }
        }
        
        // Check in available_rates
        if (room.available_rates && Array.isArray(room.available_rates)) {
          for (const rate of room.available_rates) {
            if (rate && typeof rate === 'object') {
              const rateDate = rate.date || rate.check_in;
              const rateDatePart = rateDate?.split('T')[0];
              
              if (rateDatePart === dateString && rate.final_rate > 0) {
                return false; // Date is not disabled
              }
            }
          }
        }
        
        // Check in rate_details
        if (room.rate_details && room.rate_details.final_rate > 0) {
          const rateDate = room.rate_details.date || room.rate_details.check_in || room.availability?.check_in;
          const rateDatePart = rateDate?.split('T')[0];
          
          if (rateDatePart === dateString) {
            return false; // Date is not disabled
          }
        }
      }
    }
    
    // Check if there's a price in the prices map (this is a fallback)
    if (prices[dateString] && prices[dateString] > 0) {
    return false;
    }
    
    // By default, if no availability was found, the date is disabled
    return true;
  };

  // Compact renderDay function with enhanced styling
  const renderDay = (day, month, year) => {
    if (day === null) {
      return <td className="p-0 h-12 w-12"></td>;
    }
    
    const date = new Date(year, month, day);
    const dateString = formatDate(date);
    
    // Debug: log the date we're rendering
    console.log(`Rendering day: ${dateString}`);
    
    // Get the best price for this date by checking all available options
    let bestPrice = null;
    let bestPriceColor = null;
    
    // First prioritize TIKET_V2 rates in available_rates (as requested)
    if (apiData && Array.isArray(apiData)) {
      for (const room of apiData) {
        // Check specifically for TIKET_V2 rates first in available_rates
        if (room.available_rates && Array.isArray(room.available_rates)) {
          for (const rate of room.available_rates) {
            if (rate && rate.ratecode === 'TIKET_V2' && rate.final_rate > 0 && rate.is_active !== 0) {
              console.log(`Found TIKET_V2 rate for room ${room.room_info?.room_id}:`, rate);
              
              // For available_rates, we need to check if this applies to our date by looking at dates in availability
              if (room.availability && Array.isArray(room.availability.dates)) {
                const dateEntry = room.availability.dates.find(item => {
                  const itemDatePart = item.date?.split('T')[0];
                  return itemDatePart === dateString;
                });
                
                if (dateEntry) {
                  console.log(`Date entry found for ${dateString} in room ${room.room_info?.room_id}:`, dateEntry);
                  // Check if this date has TIKET_V2 in its available_rates
                  const dateHasTiketV2 = dateEntry.available_rates?.some(r => 
                    r.ratecode === 'TIKET_V2' && r.final_rate > 0 && r.is_active !== 0
                  );
                  
                  if (dateHasTiketV2) {
                    // Find the specific TIKET_V2 rate for this date
                    const tiketV2Rate = dateEntry.available_rates.find(r => r.ratecode === 'TIKET_V2' && r.is_active !== 0);
                    if (tiketV2Rate && tiketV2Rate.final_rate > 0) {
                      const price = parseFloat(tiketV2Rate.final_rate);
                      if (!isNaN(price)) {
                        console.log(`Using TIKET_V2 price ${price} for date ${dateString} from available_rates`);
                        bestPrice = price;
                        bestPriceColor = tiketV2Rate.color || '#059669';
                        // We found a TIKET_V2 price, so no need to continue searching
                        break;
                      }
                    }
                  }
                }
              }
            }
          }
        }
        
        // If we already found a TIKET_V2 price, no need to check other rooms
        if (bestPrice !== null) break;
      }
    }
    
    // If no TIKET_V2 price found yet, check all other rates
    if (bestPrice === null && apiData && Array.isArray(apiData)) {
      // Proceed with the existing logic to find the best price
      for (const room of apiData) {
        // Check availability in the room's availability.dates structure
        if (room && room.availability && Array.isArray(room.availability.dates)) {
          const dateEntry = room.availability.dates.find(day => {
            const dayDatePart = day.date?.split('T')[0];
            return dayDatePart === dateString;
          });
          
          // Check if the date entry has a valid price and rooms to sell
          if (dateEntry) {
            console.log(`Found date ${dateString} in room ${room.room_info?.room_id}:`, dateEntry);
            
            // PRIORITY: Check available_rates array within this date (THIS IS THE KEY FIX)
            if (dateEntry.available_rates && Array.isArray(dateEntry.available_rates)) {
              for (const rate of dateEntry.available_rates) {
                if (rate && rate.final_rate > 0 && rate.is_active !== 0) {
                  const price = parseFloat(rate.final_rate);
                  if (!isNaN(price) && (bestPrice === null || price < bestPrice)) {
                    console.log(`Found price for ${dateString}: ${price} from available_rates, ratecode: ${rate.ratecode}`);
                    bestPrice = price;
                    bestPriceColor = rate.color || '#363738';
                  }
                }
              }
            }
            
            // Check if there's a direct final_rate in the date entry (fallback)
            if (dateEntry.final_rate > 0 && dateEntry.rooms_to_sell > 0) {
              const price = parseFloat(dateEntry.final_rate);
              if (!isNaN(price) && (bestPrice === null || price < bestPrice)) {
                console.log(`Found direct price for ${dateString}: ${price}`);
                bestPrice = price;
                bestPriceColor = dateEntry.color;
              }
            }
            
            // Also check if there are rates within the date entry (another fallback)
            if (dateEntry.rates && Array.isArray(dateEntry.rates)) {
              for (const rate of dateEntry.rates) {
                if (rate && rate.final_rate > 0 && rate.rooms_to_sell > 0) {
                  const price = parseFloat(rate.final_rate);
                  if (!isNaN(price) && (bestPrice === null || price < bestPrice)) {
                    console.log(`Found rate price for ${dateString}: ${price} in rates array`);
                    bestPrice = price;
                    bestPriceColor = rate.color;
                  }
                }
              }
            }
          }
        }
        
        // Also check if there are available_rates directly in the room
        if (room.available_rates && Array.isArray(room.available_rates)) {
          for (const rate of room.available_rates) {
            if (rate && typeof rate === 'object') {
              // See if this rate is for our date
              const rateDate = rate.date || rate.check_in;
              const rateDatePart = rateDate?.split('T')[0];
              
              if (rateDatePart === dateString && rate.final_rate > 0) {
                const price = parseFloat(rate.final_rate);
                if (!isNaN(price) && (bestPrice === null || price < bestPrice)) {
                  console.log(`Found price in available_rates for ${dateString}: ${price}`);
                  bestPrice = price;
                  bestPriceColor = rate.color;
                }
              }
            }
          }
        }
        
        // If the room has rate_details, check that too
        if (room.rate_details && room.rate_details.final_rate > 0) {
          // Check if this rate is for our date
          const rateDate = room.rate_details.date || room.rate_details.check_in || room.availability?.check_in;
          const rateDatePart = rateDate?.split('T')[0];
          
          if (rateDatePart === dateString) {
            const price = parseFloat(room.rate_details.final_rate);
            if (!isNaN(price) && (bestPrice === null || price < bestPrice)) {
              console.log(`Found price in rate_details for ${dateString}: ${price}`);
              bestPrice = price;
              bestPriceColor = room.rate_details.color;
            }
          }
        }
      }
    }
    
    // Fallback to the prices map if nothing was found in API data
    if (bestPrice === null && prices[dateString] > 0) {
      bestPrice = prices[dateString];
      bestPriceColor = colorMap[dateString];
    }
    
    const hasDiscount = discountMap[dateString] !== undefined;
    
    // Cek apakah tanggal ini adalah tanggal yang dipilih
    const isStartDate = selectedStartDate && day === selectedStartDate.getDate() && 
                        month === selectedStartDate.getMonth() && 
                        year === selectedStartDate.getFullYear();
    
    const isEndDate = selectedEndDate && day === selectedEndDate.getDate() && 
                      month === selectedEndDate.getMonth() && 
                      year === selectedEndDate.getFullYear();
    
    const isInRange = selectedStartDate && selectedEndDate && 
                      date >= selectedStartDate && 
                      date <= selectedEndDate;
    
    // Cek apakah tanggal ini adalah hari ini
    const today = new Date();
    const isToday = day === today.getDate() && 
                    month === today.getMonth() && 
                    year === today.getFullYear();
    
    // Cek apakah tanggal ini adalah weekend
    const isWeekendDay = isWeekend(date);
    
    // A date is disabled if it has no price or is in the past
    const isDisabled = bestPrice === null || date < new Date(new Date().setHours(0, 0, 0, 0));

    // Allow selecting the checkout date even if it has no price
    // specifically when it's exactly the next day after selectedStartDate
    const isSelectingEndDate = !!selectedStartDate && !selectedEndDate;
    const daysDiffFromStart = isSelectingEndDate 
      ? Math.ceil((date - selectedStartDate) / (1000 * 60 * 60 * 24))
      : null;
    const canSelectCheckoutNextDay = isSelectingEndDate && daysDiffFromStart === 1;
    
    // Format the price for display
    const formatPriceForDisplay = (price) => {
      if (!price || price <= 0) return "Not Available";
      
      // Format large numbers with M/K for better readability
      if (price >= 1000000) {
        return `Rp ${(price / 1000000).toFixed(1)}M`;
      } else if (price >= 1000) {
        return `Rp ${(price / 1000).toFixed(0)}K`;
      } else {
        return `Rp ${price}`;
      }
    };
    
    const formattedPrice = formatPriceForDisplay(bestPrice);
    
    // Modern cell styling with gradient and shadow effects
    let cellClass = 'relative p-1 transition-all duration-300 ease-in-out';
    
    if (isStartDate) {
      cellClass += ' bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg scale-105 rounded-xl';
    } else if (isEndDate) {
      cellClass += ' bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg scale-105 rounded-xl';
    } else if (isInRange) {
      cellClass += ' bg-blue-50/80 backdrop-blur-sm';
    } else {
      cellClass += ' hover:bg-gradient-to-br hover:from-gray-50 hover:to-gray-100 hover:shadow-md hover:scale-105';
    }
    
    if (isToday) {
      cellClass += ' ring-2 ring-blue-400 ring-offset-1';
    }
    
    const canClick = !isDisabled || canSelectCheckoutNextDay;
    if (isDisabled && !canClick) {
      cellClass += ' bg-gray-50/50 cursor-not-allowed opacity-40';
    } else {
      cellClass += ' cursor-pointer hover:z-10';
    }
    
    return (
      <td 
        className={cellClass}
        onClick={() => (canClick ? handleDateClick(date) : undefined)}
      >
        <div className="flex flex-col items-center justify-center h-12 w-full rounded-lg overflow-hidden">
          <div className={`flex flex-col items-center justify-center w-full h-full p-1 ${isStartDate || isEndDate ? 'animate-pulse-slow' : ''}`}>
            <span className={`text-sm font-bold mb-0 ${
              isStartDate || isEndDate ? 'text-white drop-shadow-sm' : 
              isWeekendDay ? 'text-red-500 font-extrabold' : 
              isDisabled ? 'text-gray-300' : 
              'text-gray-800'
            }`}>
              {day}
            </span>
            
            {bestPrice !== null && (
              <div className="flex flex-col items-center">
                <span 
                  className={`text-[9px] font-semibold leading-tight ${
                    isStartDate || isEndDate ? 'text-white/95 drop-shadow-sm' : 
                    hasDiscount ? 'text-green-600' : 
                    isDisabled ? 'text-gray-300' : 
                    'text-gray-600'
                  }`}
                  style={isStartDate || isEndDate ? {} : { color: bestPriceColor || '#4B5563' }}
                >
                  {formattedPrice}
                </span>
                {hasDiscount && !isStartDate && !isEndDate && (
                  <span className="text-[7px] font-semibold text-green-600 bg-green-50 px-0.5 rounded">
                    -{discountMap[dateString]?.value}%
                  </span>
                )}
              </div>
            )}
            
            {isToday && !isStartDate && !isEndDate && (
              <div className="absolute top-0.5 right-0.5 w-1.5 h-1.5 bg-blue-500 rounded-full animate-ping"></div>
            )}
          </div>
        </div>
      </td>
    );
  };

  // Perbaiki handler untuk klik tanggal
  const handleDateClick = (date) => {
    if (!date) return;
    
    const dateString = formatDate(date);
    
    // Check if date is in the past
    const isPastDate = date < new Date(new Date().setHours(0, 0, 0, 0));
    if (isPastDate) return;
    
    const isSelectingEndDate = !!selectedStartDate && !selectedEndDate;
    
    // Check availability only when selecting the start date.
    // For selecting the end date (checkout), allow even if the date has no price.
    if (!isSelectingEndDate) {
      let hasAvailability = false;
      
      if (apiData && Array.isArray(apiData)) {
        for (const room of apiData) {
          // Check availability in the room's availability.dates
          if (room && room.availability && Array.isArray(room.availability.dates)) {
            const dateEntry = room.availability.dates.find(day => {
              const dayDatePart = day.date?.split('T')[0];
              return dayDatePart === dateString;
            });
            
            // PRIORITY: Check available_rates array within this date
            if (dateEntry && dateEntry.available_rates && Array.isArray(dateEntry.available_rates)) {
              for (const rate of dateEntry.available_rates) {
                if (rate && rate.final_rate > 0 && rate.is_active !== 0) {
                  hasAvailability = true;
                  break;
                }
              }
              if (hasAvailability) break;
            }
            
            // Check direct final_rate (fallback)
            if (dateEntry && dateEntry.final_rate > 0 && dateEntry.rooms_to_sell > 0) {
              hasAvailability = true;
              break;
            }
            
            // Check rates array (another fallback)
            if (dateEntry && dateEntry.rates && Array.isArray(dateEntry.rates)) {
              for (const rate of dateEntry.rates) {
                if (rate && rate.final_rate > 0 && rate.rooms_to_sell > 0) {
                  hasAvailability = true;
                  break;
                }
              }
              if (hasAvailability) break;
            }
          }
          
          // Check available_rates
          if (!hasAvailability && room.available_rates && Array.isArray(room.available_rates)) {
            for (const rate of room.available_rates) {
              if (rate && typeof rate === 'object') {
                const rateDate = rate.date || rate.check_in;
                const rateDatePart = rateDate?.split('T')[0];
                
                if (rateDatePart === dateString && rate.final_rate > 0) {
                  hasAvailability = true;
                  break;
                }
              }
            }
            if (hasAvailability) break;
          }
          
          // Check rate_details
          if (!hasAvailability && room.rate_details && room.rate_details.final_rate > 0) {
            const rateDate = room.rate_details.date || room.rate_details.check_in || room.availability?.check_in;
            const rateDatePart = rateDate?.split('T')[0];
            
            if (rateDatePart === dateString) {
              hasAvailability = true;
              break;
            }
          }
        }
      }
      
      // If no availability in API data, check the prices map as fallback
      if (!hasAvailability && (!prices[dateString] || prices[dateString] <= 0)) {
        return; // Don't allow start-date selection if no availability
      }
    }

    if (!selectedStartDate || (selectedStartDate && selectedEndDate)) {
      // Mulai pemilihan baru
      setSelectedStartDate(date);
      setSelectedEndDate(null);
    } else {
      // Sudah ada tanggal mulai, pilih tanggal akhir
      if (date < selectedStartDate) {
        // Jika tanggal yang dipilih sebelum tanggal mulai, jadikan sebagai tanggal mulai baru
        setSelectedStartDate(date);
        setSelectedEndDate(null);
      } else if (formatDate(date) === formatDate(selectedStartDate)) {
        // Jika tanggal yang sama dipilih, tidak lakukan apa-apa
        return;
      } else {
        // Pilih tanggal akhir dan pastikan minimal 1 malam
        const endDate = new Date(date);
        const startDate = new Date(selectedStartDate);
        
        // Hitung selisih hari
        const diffTime = Math.abs(endDate - startDate);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        if (diffDays < 1) {
          // Jika kurang dari 1 malam, tambahkan 1 hari ke tanggal akhir
          endDate.setDate(endDate.getDate() + 1);
        }
        
        setSelectedEndDate(endDate);
        onDateSelect(selectedStartDate, endDate);
      }
    }
  };

  // Check if a date is in the selected range
  const isDateInRange = (date) => {
    if (!date || !selectedStartDate || !selectedEndDate) return false;
    return date >= selectedStartDate && date <= selectedEndDate;
  };

  // Handle "Done" button click
  const handleDoneClick = () => {
    if (selectedStartDate && selectedEndDate) {
      onDateSelect(selectedStartDate, selectedEndDate);
      onClose();
    } else if (selectedStartDate) {
      // Jika hanya tanggal mulai yang dipilih, gunakan itu sebagai tanggal akhir juga
      onDateSelect(selectedStartDate, selectedStartDate);
      onClose();
    }
  };

  // Handle Book Now button click
  const handleBookNow = () => {
    if (selectedStartDate && selectedEndDate) {
      onDateSelect(selectedStartDate, selectedEndDate);
      onClose();
    }
  };

  // Compact renderMonth function with enhanced styling
  const renderMonth = (calendar, date) => {
    return (
      <div className={`${isMobile ? 'mb-4' : ''}`}>
        <div className="text-center font-bold mb-2 text-gray-900 text-base tracking-wide">
          {MONTHS[date.getMonth()]} {date.getFullYear()}
        </div>
        <table className="border-collapse w-full border-spacing-0">
          <thead>
            <tr>
              {DAYS.map((day, index) => (
                <th 
                  key={`header-${date.getMonth()}-${index}`}
                  className={`p-1 text-center text-[10px] font-bold uppercase tracking-wide w-12 ${
                    index === 0 || index === 6 ? 'text-red-500' : 'text-gray-500'
                  }`}
                >
                  {day}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="rounded-xl overflow-hidden">
            {calendar.map((week, weekIndex) => (
              <tr key={`week-${date.getMonth()}-${weekIndex}`}>
                {week.map((day, dayIndex) => (
                  <React.Fragment key={`day-${date.getMonth()}-${weekIndex}-${dayIndex}`}>
                    {renderDay(day, date.getMonth(), date.getFullYear())}
                  </React.Fragment>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Tambahkan useEffect untuk memperbarui kalender saat prices berubah
  useEffect(() => {
    // Update kalender saat data harga berubah
    if (Object.keys(prices).length > 0) {
      console.log('Updating calendar with price data, prices count:', Object.keys(prices).length);
      
      // Force re-render of calendars
      updateCalendars();
      
      // Force component re-render by updating a state
      setCurrentDate(prev => new Date(prev.getTime()));
    }
  }, [prices]);

  // Tambahkan fungsi untuk debug
  useEffect(() => {
    // Debug: log prices setiap kali berubah
    console.log('Prices updated:', Object.keys(prices).length, 'entries');
    
    // Debug: log colorMap
    console.log('Color map updated:', Object.keys(colorMap).length, 'entries');
    
    // Tampilkan beberapa contoh harga
    const priceEntries = Object.entries(prices).slice(0, 5);
    if (priceEntries.length > 0) {
      console.log('Sample prices:', priceEntries);
      
      // Check if prices are numbers
      priceEntries.forEach(([date, price]) => {
        console.log(`Price for ${date}: ${price}, type: ${typeof price}, color: ${colorMap[date]}`);
      });
    }
  }, [prices, colorMap]);

  // Tambahkan useEffect untuk memperbarui kalender saat komponen dimount
  useEffect(() => {
    // Inisialisasi kalender
    updateCalendars();
    
    // Ambil data harga untuk 2 bulan pertama
    fetchRoomData();
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Add this function after other event handlers
  const handleCalendarClick = (e) => {
    // Prevent event from bubbling up to document
    e.stopPropagation();
    
    // Prevent default browser behavior that might cause scrolling
    e.preventDefault();
  };

  // Modern main return statement with glassmorphism effect
  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-black/70 to-black/50 backdrop-blur-md animate-fadeIn"
      onClick={onClose}
    >
      <div 
        className="relative bg-white/95 backdrop-blur-xl rounded-2xl w-full max-w-[95%] sm:max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl border border-white/20 animate-slideUp"
        onClick={handleCalendarClick}
      >
        {/* Modern close button - Hidden on mobile to avoid blocking navigation */}
        <button
          className="hidden sm:block absolute top-4 right-4 p-2 rounded-full bg-gray-100/80 hover:bg-red-100 hover:text-red-600 transition-all duration-200 z-10 group shadow-sm"
          onClick={onClose}
        >
          <svg className="w-5 h-5 text-gray-600 group-hover:text-red-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="p-4 sm:p-6">
          <div className="flex flex-col">
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <div className={`${showTwoMonths ? 'hidden sm:block' : 'block'} w-full sm:w-1/2 bg-gradient-to-br from-gray-50 to-white p-3 rounded-xl shadow-sm`}>
                  <div className="flex items-center justify-between mb-2">
                    <button
                      onClick={goToPreviousMonth}
                      className={`p-2 rounded-lg transition-all duration-200 ${isCurrentMonth ? 'text-gray-300 cursor-not-allowed' : 'text-gray-700 hover:bg-white hover:shadow-md'}`}
                      disabled={isCurrentMonth}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    
                    <h4 className="font-bold text-gray-900">{months[currentDate.getMonth()]} {currentDate.getFullYear()}</h4>
                    
                    <button
                      onClick={goToNextMonth}
                      className="p-2 rounded-lg text-gray-700 hover:bg-white hover:shadow-md transition-all duration-200"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                  {renderCalendar(currentDate.getMonth(), currentDate.getFullYear())}
                </div>
                
                {showTwoMonths && (
                  <div className="hidden sm:block w-full sm:w-1/2 bg-gradient-to-br from-gray-50 to-white p-3 rounded-xl shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                      <div className="w-5"></div>
                      <h4 className="font-bold text-gray-900">{months[(currentDate.getMonth() + 1) % 12]} {currentDate.getMonth() === 11 ? currentDate.getFullYear() + 1 : currentDate.getFullYear()}</h4>
                      <button
                        onClick={goToNextMonth}
                        className="p-2 rounded-lg text-gray-700 hover:bg-white hover:shadow-md transition-all duration-200"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                    {renderCalendar((currentDate.getMonth() + 1) % 12, currentDate.getMonth() === 11 ? currentDate.getFullYear() + 1 : currentDate.getFullYear())}
                  </div>
                )}
              </div>
              
              {/* Mobile month navigation */}
              <div className="flex justify-center mt-3 sm:hidden">
                <div className="inline-flex rounded-md shadow-sm">
                  <button 
                    onClick={goToPreviousMonth} 
                    className={`px-4 py-2 rounded-l-lg border ${isCurrentMonth ? 'bg-gray-50 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                    disabled={isCurrentMonth}
                  >
                    {t.previous}
                  </button>
                  <button 
                    onClick={goToNextMonth} 
                    className="px-4 py-2 rounded-r-lg border-t border-r border-b bg-white text-gray-700 hover:bg-gray-50"
                  >
                    {t.next}
                  </button>
                </div>
              </div>
              
              {/* Compact price legend */}
              <div className="mt-4 pt-3 border-t border-gray-200">
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-2">
                  <div className="flex flex-wrap gap-2 items-center justify-center">
                    <span className="text-[10px] text-gray-700 font-semibold">Legend:</span>
                    <div className="flex items-center gap-1 bg-white px-1.5 py-0.5 rounded shadow-sm">
                      <span className="inline-block w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                      <span className="text-[10px] text-gray-700 font-medium">Weekend</span>
                    </div>
                    <div className="flex items-center gap-1 bg-white px-1.5 py-0.5 rounded shadow-sm">
                      <span className="inline-block w-2 h-2 bg-green-500 rounded-full"></span>
                      <span className="text-[10px] text-gray-700 font-medium">Discount</span>
                    </div>
                    <div className="flex items-center gap-1 bg-white px-1.5 py-0.5 rounded shadow-sm">
                      <span className="inline-block w-2 h-2 bg-blue-500 rounded-full"></span>
                      <span className="text-[10px] text-gray-700 font-medium">Selected</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Compact footer section */}
            <div className="mt-4 pt-3 border-t border-gray-200">
              <div className="flex flex-col sm:flex-row justify-between items-center gap-2">
                <p className="text-[10px] text-gray-500 italic flex items-center gap-1">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  *Terms and conditions apply
                </p>
                
                <div className="flex items-center gap-2">
                  {selectedStartDate && selectedEndDate && (
                    <button 
                      onClick={handleBookNow}
                      className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white text-xs font-semibold rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 flex items-center gap-1 shadow-lg hover:shadow-xl transform hover:scale-105"
                    >
                      {t.bookNow}
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    </button>
                  )}
                  
                  <button 
                    onClick={onClose}
                    className="px-4 py-1.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-200 transition-all duration-200"
                  >
                    {t.close}
                  </button>
                </div>
              </div>
              
              {/* Selection status */}
              {(selectedStartDate || selectedEndDate) && (
                <p className="text-[10px] text-blue-600 mt-1 text-center">
                  {selectedStartDate && !selectedEndDate ? t.selectCheckOutDate : !selectedStartDate ? t.selectCheckInDate : ''}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
  
  );
};

export default CustomCalendar;
